import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Users, Mail, PhoneCall, Target, ArrowRightLeft, CalendarDays, Trash2, FlaskConical } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EventComparisonChart } from "@/components/leaddesk/EventComparisonChart";

function Stat({ icon: Icon, label, value, sub, tone = "text-slate-900", testid }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 min-w-[118px]">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</p>
      <p className={`font-mono text-xl font-semibold ${tone}`} data-testid={testid}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
  );
}

function Bar({ value, total, tone }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return <div className="h-1.5 rounded bg-slate-100 overflow-hidden"><div className={`h-full ${tone}`} style={{ width: `${pct}%` }} /></div>;
}

export function EventDashboard({ brand }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [scope, setScope] = useState(() => (["marketing", "super_admin", "sales_head", "management"].includes(user?.role) ? "team" : "mine"));
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["ld-events-dash", scope, brand], queryFn: async () => (await api.get("/lead-desk/events-dashboard", { params: { scope, brand: brand || "" } })).data });
  const events = data?.events || [];
  const refreshAll = () => ["ld-events-dash", "ld-register", "leads-table", "leads-kanban", "contacts", "leads-summary", "events"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const sample = async (load) => {
    setBusy(true);
    try {
      const r = load ? await api.post("/lead-desk/sample-data") : await api.delete("/lead-desk/sample-data");
      toast.success(load ? `Loaded ${r.data.created} sample visitors across ${r.data.events.length} exhibitions` : "Sample data removed");
      refreshAll();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="event-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">One card per exhibition — visitors captured at the Lead Desk, catalogue emails sent, contacts called, leads and conversions to Opportunities.</p>
        <div className="flex items-center gap-2">
          {data?.can_manage_sample && (
            data.has_sample
              ? <Button size="sm" variant="outline" className="h-9 gap-1.5 text-xs" disabled={busy} onClick={() => sample(false)} data-testid="ld-remove-sample"><Trash2 className="h-3.5 w-3.5" /> Remove sample data</Button>
              : <Button size="sm" variant="outline" className="h-9 gap-1.5 text-xs" disabled={busy} onClick={() => sample(true)} data-testid="ld-load-sample"><FlaskConical className="h-3.5 w-3.5" /> Load sample data</Button>
          )}
          {data?.can_team && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="ld-events-scope"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="mine">My events</SelectItem><SelectItem value="team">{["marketing", "super_admin", "sales_head"].includes(user?.role) ? "All reps" : "My team"}</SelectItem></SelectContent>
            </Select>
          )}
        </div>
      </div>
      {data?.has_sample && <p className="text-[11px] text-slate-400 -mt-2" data-testid="ld-sample-note">Includes <b>sample</b> visitors for visualisation — rows are tagged "Sample" and can be removed in one click.</p>}
      {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : !events.length ? (
        <Card className="p-10 text-center text-sm text-slate-400 border-slate-200">No exhibitions yet — capture a visitor at the Lead Desk or scan a card with an event name.</Card>
      ) : (<>
        <EventComparisonChart events={events} />
        {events.map((e) => (
        <Card key={e.event} className="p-4 border-slate-200" data-testid={`event-card-${e.event.replace(/\s+/g, "-")}`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-brand" /> {e.event}</h3>
              <p className="text-xs text-slate-400">Last activity {e.last_at ? new Date(e.last_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</p>
            </div>
            <Link to={`/contacts?event=${encodeURIComponent(e.event)}&followup=pending`} className="text-xs text-indigo-600 hover:text-indigo-800" data-testid={`event-open-pending-${e.event.replace(/\s+/g, "-")}`}>Open {e.pending} pending contact{e.pending === 1 ? "" : "s"} →</Link>
          </div>
          <div className="flex flex-wrap gap-2">
            <Stat icon={Users} label="Visitors" value={e.visitors} sub="captured at desk" testid="ev-visitors" />
            <Stat icon={Mail} label="Catalogues" value={e.emails_sent} sub="emails sent" tone="text-emerald-700" testid="ev-emails" />
            <Stat icon={PhoneCall} label="Called" value={`${e.called}/${e.contacts}`} sub={`${e.pending} pending`} tone={e.pending ? "text-amber-600" : "text-emerald-700"} testid="ev-called" />
            <Stat icon={Target} label="Leads" value={e.leads} sub={e.lost ? `${e.lost} lost` : "created"} testid="ev-leads" />
            <Stat icon={ArrowRightLeft} label="Converted" value={e.converted} sub="to opportunity" tone="text-indigo-700" testid="ev-converted" />
          </div>
          {e.reps.length > 0 && (
            <table className="mt-4 w-full text-xs" data-testid="ev-reps">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left"><th className="py-1 pr-2">Rep</th><th className="py-1 pr-2 text-right">Visitors</th><th className="py-1 pr-2 text-right">Emails</th><th className="py-1 pr-2 w-40">Called</th><th className="py-1 pr-2 text-right">Leads</th><th className="py-1 text-right">Converted</th></tr></thead>
              <tbody>
                {e.reps.map((r) => (
                  <tr key={r.user_id} className="border-t border-slate-100">
                    <td className="py-1.5 pr-2 font-medium text-slate-800">{r.name || "—"}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{r.visitors}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{r.emails_sent}</td>
                    <td className="py-1.5 pr-2"><div className="flex items-center gap-2"><span className="font-mono w-10 text-right">{r.called}/{r.contacts}</span><div className="flex-1"><Bar value={r.called} total={r.contacts} tone="bg-emerald-500" /></div></div></td>
                    <td className="py-1.5 pr-2 text-right font-mono">{r.leads}</td>
                    <td className="py-1.5 text-right font-mono text-indigo-700">{r.converted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ))}
      </>)}
    </div>
  );
}
